import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, Row, Col, Switch, Divider, Select, Tooltip, message } from 'antd';
import { useQuery, useLazyQuery, useMutation, gql } from '@apollo/client';
import { SEARCH_CITIES } from '../../mutations/address';
import useDebounce from '../../hooks/useDebounce';
import { CloseCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined, CloseCircleFilled, SyncOutlined } from '@ant-design/icons';
import './CustomerForm.css';
import { GET_CUSTOMER_BY_ID, GET_ACTIVE_PRICE_SEGMENTATIONS, GET_ALL_PRICE_SEGMENTATIONS, GET_ACTIVE_TAGS, useVerifyCustomerWhatsapp } from '../../mutations/customers';
import { GET_EVOLUTION_STATUS } from '../../mutations/account';

const { Option } = Select;

const GET_ACCOUNT_WHATSAPP_SETTINGS = gql`
  query GetAccountWhatsappSettings {
    account {
      whatsappProvider
      plugchatConnected
    }
  }
`;

// Função auxiliar para converter uma data ISO para o formato datetime-local mantendo o timezone local
const formatDateForInput = (isoDateString) => {
  if (!isoDateString) return null;

  const date = new Date(isoDateString);
  // Ajustar para o timezone local
  const timezoneOffset = date.getTimezoneOffset() * 60000;
  const localDate = new Date(date.getTime() - timezoneOffset);

  return localDate.toISOString().slice(0, 16);
};

// Função auxiliar para converter o valor do input datetime-local para ISO string
const formatDateForSubmission = (datetimeLocalString) => {
  if (!datetimeLocalString) return null;

  // O input datetime-local já está no timezone local, então podemos converter diretamente
  const date = new Date(datetimeLocalString);
  return date.toISOString();
};

// Função auxiliar para converter uma data ISO para o formato date
const formatDateOnlyForInput = (isoDateString) => {
  if (!isoDateString) return null;

  const date = new Date(isoDateString);
  return date.toISOString().slice(0, 10); // YYYY-MM-DD
};

// Função auxiliar para converter o valor do input date para ISO string
const formatDateOnlyForSubmission = (dateString) => {
  if (!dateString) return null;

  return dateString; // O input date já retorna no formato YYYY-MM-DD
};

const CustomerForm = ({ initialValues = {}, onFinish, onCancel, errorMessage: initialErrorMessage, isEditMode = false }) => {
  const [form] = Form.useForm();
  const [getCustomerById] = useLazyQuery(GET_CUSTOMER_BY_ID);
  const botStatus = Form.useWatch('botStatus', form);
  const [selectedCustomer, setSelectedCustomer] = useState(initialValues);
  const [errorMessage, setErrorMessage] = useState(initialErrorMessage);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [priceSegmentations, setPriceSegmentations] = useState([]);
  const { loading: priceLoading, data: priceData } = useQuery(GET_ACTIVE_PRICE_SEGMENTATIONS, {
    fetchPolicy: 'cache-and-network'
  });
  const { data: accountData } = useQuery(GET_ACCOUNT_WHATSAPP_SETTINGS, {
    fetchPolicy: 'cache-and-network'
  });
  const { data: evolutionStatusData } = useQuery(GET_EVOLUTION_STATUS, {
    fetchPolicy: 'cache-and-network',
    skip: accountData?.account?.whatsappProvider !== 'evolution'
  });
  const [getAllPriceSegmentations, { data: allPriceData }] = useLazyQuery(GET_ALL_PRICE_SEGMENTATIONS, {
    fetchPolicy: 'cache-and-network'
  });
  const { loading: tagsLoading, data: tagsData } = useQuery(GET_ACTIVE_TAGS, {
    fetchPolicy: 'network-only'
  });
  const [tags, setTags] = useState([]);

  // WhatsApp verification
  const [verifyWhatsapp, { loading: verifyingWhatsapp }] = useVerifyCustomerWhatsapp();
  const [whatsappStatus, setWhatsappStatus] = useState({
    verified: false,
    exists: false,
    checked: false
  });
  const whatsappProvider = accountData?.account?.whatsappProvider;
  const isWhatsappProviderConnected = whatsappProvider === 'evolution'
    ? evolutionStatusData?.evolutionStatus?.evolutionConnected
    : accountData?.account?.plugchatConnected;

  const initialEmails = initialValues.emails || [];
  const initialAddresses = initialValues.addresses || [];

  const [searchCities, { data: citiesData }] = useLazyQuery(SEARCH_CITIES, {
    fetchPolicy: 'network-only'
  });
  const [cities, setCities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 1000); // 1000ms delay

  useEffect(() => {
    if (priceData && priceData.activePriceSegmentations) {
      let segmentations = [...priceData.activePriceSegmentations];
      
      // Se temos dados de todas as tabelas, mesclar com as ativas
      if (allPriceData && allPriceData.priceSegmentations && allPriceData.priceSegmentations.edges) {
        const allSegmentations = allPriceData.priceSegmentations.edges.map(edge => edge.node);
        
        // Adicionar tabelas inativas que o cliente possui selecionadas
        if (selectedCustomer && selectedCustomer.priceSegmentationIds) {
          selectedCustomer.priceSegmentationIds.forEach(selectedId => {
            const existsInActive = segmentations.find(activeSeg => activeSeg.id === selectedId);
            if (!existsInActive) {
              const inactiveSegmentation = allSegmentations.find(seg => seg.id === selectedId);
              if (inactiveSegmentation) {
                segmentations.push(inactiveSegmentation);
              }
            }
          });
        }
      }
      
      setPriceSegmentations(segmentations);
    }
  }, [priceData, allPriceData, selectedCustomer]);

  useEffect(() => {
    if (tagsData && tagsData.activeTags) {
      setTags(tagsData.activeTags);
    }
  }, [tagsData]);

  useEffect(() => {
    if (debouncedSearchTerm.length > 2) {
      searchCities({ variables: { query: debouncedSearchTerm } });
    }
  }, [debouncedSearchTerm, searchCities]);

  useEffect(() => {
    if (citiesData) {
      setCities(citiesData.searchCities);
    }
  }, [citiesData]);

  const handleSearchCity = (value) => {
    setSearchTerm(value);
  };

  useEffect(() => {
    form.resetFields();
    if (isEditMode) {
      getCustomerById({ variables: { id: initialValues.id } }).then((response) => {
        console.log(response.data.customer)
        const customer = response?.data?.customer;
        if (customer) {
          const botStatus = customer.botStatus === 'bot_active';
          const addressesAttributes = customer.addresses.map(address => ({
            ...address,
            cityId: { value: address?.city?.id, label: `${address?.city?.name} - ${address?.city?.state?.initials}` }
          }));
          const emailsAttributes = customer.emails.map(email => ({
            ...email,
          }));

          // Formatar a data do last_sale para o formato do input datetime-local
          const lastSale = customer.lastSale
            ? formatDateForInput(customer.lastSale)
            : null;

          // Formatar a data de nascimento para o formato do input date
          const birthDate = customer.birthDate
            ? formatDateOnlyForInput(customer.birthDate)
            : null;

          form.setFieldsValue({
            ...customer,
            addressesAttributes,
            emailsAttributes,
            botStatus,
            lastSale,
            birthDate,
            priceSegmentationIds: customer.priceSegmentationIds,
            tagIds: customer.tagIds
          });

          // Atualizar status do WhatsApp
          setWhatsappStatus({
            verified: customer.whatsappVerified || false,
            exists: customer.whatsappVerified || false, // Se verificado, assume que existe
            checked: customer.whatsappVerified !== null // Se foi checado alguma vez
          });

          setSelectedCustomer(customer);
          
          // Carregar todas as tabelas de preço para poder mostrar as inativas selecionadas
          if (customer.priceSegmentationIds && customer.priceSegmentationIds.length > 0) {
            getAllPriceSegmentations();
          }
        } else {
          form.resetFields();
        }
      });
    } else {
      form.resetFields();
      // Reset WhatsApp status for new customer
      setWhatsappStatus({
        verified: false,
        exists: false,
        checked: false
      });
    }
  }, [initialValues, isEditMode, form, getCustomerById]);

  useEffect(() => {
    setErrorMessage(initialErrorMessage);
  }, [initialErrorMessage]);

  useEffect(() => {
    if (initialValues.city) {
      setCities([{ id: initialValues.city.id, name: initialValues.city.name, state: initialValues.city.state }]);
    }
  }, [initialValues]);

  const handleFormCancel = () => {
    form.resetFields();
    form.setFieldsValue({});
    if (onCancel) onCancel();
  };

  const handleCancel = handleFormCancel;

  const handleSave = async (values) => {
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setErrorMessage(null);
    
    // Converter botStatus para string corretamente
    // Se for boolean: true → 'bot_active', false → 'bot_disabled'
    // Se já for string, manter como está
    if (typeof values.botStatus === 'boolean') {
      values.botStatus = values.botStatus ? 'bot_active' : 'bot_disabled';
    }

    // Garantir que o lastSale seja enviado no formato ISO8601
    if (values.lastSale) {
      values.lastSale = formatDateForSubmission(values.lastSale);
    }

    // Garantir que o birthDate seja enviado no formato correto
    if (values.birthDate) {
      values.birthDate = formatDateOnlyForSubmission(values.birthDate);
    }

    const processedValues = cleanData(values);

    // Chamar a função onFinish com os valores processados
    try {
      if (typeof onFinish === 'function') {
        await onFinish(processedValues);
      }
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      setErrorMessage('Ocorreu um erro ao salvar o cliente.');
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleVerifyWhatsapp = async () => {
    const whatsappNumber = form.getFieldValue('whatsapp');

    if (!whatsappNumber || whatsappNumber.trim() === '') {
      message.error('Por favor, digite um número de WhatsApp');
      return;
    }

    try {
      const variables = {
        whatsappNumber: whatsappNumber.trim()
      };

      // Se o cliente já existe, inclui o ID para atualizar o registro
      if (selectedCustomer?.id) {
        variables.customerId = selectedCustomer.id;
      }

      const { data } = await verifyWhatsapp({ variables });

      if (data?.verifyCustomerWhatsapp?.success) {
        const whatsappVerified = data.verifyCustomerWhatsapp.whatsappVerified;
        setWhatsappStatus({
          verified: whatsappVerified,
          exists: whatsappVerified,
          checked: true
        });

        message.success(data.verifyCustomerWhatsapp.message);
      } else {
        message.error('Erro ao verificar WhatsApp');
      }
    } catch (error) {
      console.error('Erro ao verificar WhatsApp:', error);
      message.error('Erro ao verificar WhatsApp');
    }
  };

  const getWhatsappIcon = () => {
    if (!whatsappStatus.checked) {
      return (
        <Tooltip title="WhatsApp não verificado - clique no botão para verificar">
          <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '16px' }} />
        </Tooltip>
      );
    }

    if (whatsappStatus.verified) {
      return (
        <Tooltip title="WhatsApp verificado e válido">
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
        </Tooltip>
      );
    }

    return (
      <Tooltip title="WhatsApp verificado mas não existe">
        <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: '16px' }} />
      </Tooltip>
    );
  };

  const cleanData = (values) => {
    return {
      ...values,
      emailsAttributes: values?.emailsAttributes?.length > 0 ? values.emailsAttributes.map(email => ({
        email: email.email,
        description: email.description,
        active: email.active,
      })) : [],
      addressesAttributes: values?.addressesAttributes?.length > 0 ? values.addressesAttributes.map(address => ({
        id: address.id,
        zipcode: address.zipcode,
        cityId: address.cityId.value || address.cityId,
        description: address.description,
        district: address.district,
        street: address.street,
        number: address.number,
        complement: address.complement,
        main: address.main,
      })) : [],
      // Garantir que o lastSale seja enviado no formato correto ou null
      lastSale: values.lastSale ? values.lastSale : null,
      // Garantir que o birthDate seja enviado no formato correto ou null
      birthDate: values.birthDate ? values.birthDate : null,
    };
  };


  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSave}
      initialValues={{ ...initialValues, emails: initialEmails, addresses: initialAddresses }}
    >
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="name"
            label="Nome"
            rules={[{ required: true, message: 'Por favor, insira a razão social ou nome' }]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="nickname" label="Apelido">
            <Input />
          </Form.Item>
        </Col>

      </Row>
      <Row gutter={16}>
        <Col span={10}>
          <Form.Item
            name="document"
            label="Documento"
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={10}>
          <Form.Item name="stateRegistration" label="Inscrição Estadual">
            <Input />
          </Form.Item>
        </Col>

        <Col span={4}>
          <Form.Item
            name="birthDate"
            label="Data de Nascimento"
            rules={[
              {
                validator: (_, value) => {
                  if (value && new Date(value) > new Date()) {
                    return Promise.reject(new Error('A data de nascimento não pode ser futura'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input type="date" placeholder="Selecione a data de nascimento" max={formatDateOnlyForInput(new Date().toISOString())} />
          </Form.Item>
        </Col>
        <Col span={12}>
          {/* Espaço reservado para futuros campos */}
        </Col>
      </Row>

      {errorMessage && (
        <Row>
          <Col span={24}>
            <div style={{ color: 'red', marginBottom: '16px' }}>{errorMessage}</div>
          </Col>
        </Row>
      )}

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            name="phone"
            label="Telefone Principal"
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="phoneExtra" label="Telefone Secundário">
            <Input />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="whatsapp"
            label={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                WhatsApp
                {getWhatsappIcon()}
              </div>
            }
            rules={[{ required: true, message: 'Por favor, insira o WhatsApp' }]}
          >
            <Input
              addonAfter={
                <Button
                  size="small"
                  type="link"
                  icon={<SyncOutlined />}
                  loading={verifyingWhatsapp}
                  onClick={handleVerifyWhatsapp}
                  disabled={!isWhatsappProviderConnected}
                  style={{ padding: '0 4px' }}
                >
                  Verificar
                </Button>
              }
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="mainContact" label="Contato Principal">
            <Input placeholder="Contato principal do cliente" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="observation" label="Observação">
            <Input.TextArea />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="lastSale"
            label="Última Venda"
            rules={[
              {
                validator: (_, value) => {
                  if (value && new Date(value) > new Date()) {
                    return Promise.reject(new Error('A data da última venda não pode ser futura'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input type="datetime-local" placeholder="Selecione a data da última venda" max={formatDateForInput(new Date().toISOString())} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="favoriteProduct" label="Produto Favorito">
            <Input placeholder="Informe o produto favorito do cliente" />
          </Form.Item>
        </Col>
      </Row>



      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="priceSegmentationIds" label="Tabelas de preço" >
            <Select
              placeholder="Selecione as tabelas de preço"
              mode="multiple"  // Permite seleção de múltiplos places
              loading={priceLoading}
            >
              {priceSegmentations.map(price => (
                <Select.Option key={price.id} value={price.id}>
                  {price.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="tagIds" label="Tags" >
            <Select
              placeholder="Selecione as tags"
              mode="multiple"
              loading={tagsLoading}
              optionRender={(option) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div
                    style={{
                      padding: '2px 6px',
                      borderRadius: '4px',
                      backgroundColor: option.data.backgroundColor || '#6fa8dc',
                      color: option.data.fontColor || '#000000',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    {option.data.name}
                  </div>
                </div>
              )}
            >
              {tags.map(tag => (
                <Select.Option
                  key={tag.id}
                  value={tag.id}
                  backgroundColor={tag.backgroundColor}
                  fontColor={tag.fontColor}
                  name={tag.name}
                >
                  {tag.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left" orientationMargin="0" plain>
        Emails
      </Divider>
      <Form.List name="emailsAttributes" initialValue={initialEmails}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, fieldKey, ...restField }) => (
              <Row gutter={16} key={key}>
                <Col span={10}>
                  <Form.Item
                    {...restField}
                    name={[name, 'email']}
                    key={[fieldKey, 'email']}
                    label="Email"
                    rules={[{ required: true, message: 'Por favor, insira o email' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'description']}
                    key={[fieldKey, 'description']}
                    label="Descrição"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col
                >
                  <Form.Item
                    {...restField}
                    name={[name, 'active']}
                    key={[fieldKey, 'active']}
                    valuePropName="checked"
                    label="Ativo"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col style={{ textAlign: 'center' }}>
                  <Button
                    type="text"
                    className='remove-email'
                    icon={<CloseCircleOutlined style={{ color: 'red' }} />}
                    onClick={() => remove(name)}
                  />
                </Col>

              </Row>
            ))}
            <Button type="dashed" onClick={() => add()} block>
              Adicionar Email
            </Button>
          </>
        )}
      </Form.List>
      <Divider orientation="left" orientationMargin="0" plain>
        Endereços
      </Divider>
      <Form.List name="addressesAttributes" initialValue={initialAddresses}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <div key={key} style={{ marginBottom: 24 }}>
                <Row gutter={16}>
                  <Col span={22}>
                    <Row gutter={16}>
                      <Col span={5}>
                        <Form.Item
                          {...restField}
                          name={[name, 'zipcode']}
                          key={[key, 'zipcode']}
                          label="CEP"
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item
                          {...restField}
                          name={[name, 'cityId']}
                          key={[key, 'cityId']}
                          label="Cidade"
                          rules={[{ required: true, message: 'Por favor, selecione a cidade' }]}
                        >
                          <Select
                            showSearch
                            placeholder="Pesquisar cidade"
                            onSearch={handleSearchCity}
                            filterOption={false}
                            options={cities.map(city => ({ value: city.id, label: `${city.name} - ${city?.state?.initials}` }))}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, 'district']}
                          key={[key, 'district']}
                          label="Bairro"
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>

                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, 'street']}
                          key={[key, 'street']}
                          label="Rua"
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <Form.Item
                          {...restField}
                          name={[name, 'number']}
                          key={[key, 'number']}
                          label="Número"
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, 'description']}
                          key={[key, 'description']}
                          label="Descrição"
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <Form.Item
                          {...restField}
                          name={[name, 'main']}
                          key={[key, 'main']}
                          label="Principal"
                          valuePropName="checked"
                        >
                          <Switch />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item
                          {...restField}
                          name={[name, 'complement']}
                          key={[key, 'complement']}
                          label="Complemento"
                        >
                          <Input.TextArea allowClear={true} />
                        </Form.Item>
                      </Col>

                    </Row>
                  </Col>
                  <Col style={{ textAlign: 'center', marginTop: '90px' }} span={2}>
                    <Button
                      type="text"
                      className='remove-email'
                      icon={<CloseCircleOutlined style={{ color: 'red' }} />}
                      onClick={() => remove(name)}
                    />
                  </Col>
                </Row>


              </div>
            ))}
            <Button type="dashed" onClick={() => add()} block>
              Adicionar Endereço
            </Button>
          </>
        )}
      </Form.List>

      <Divider>Status do bot para o cliente</Divider>
      <Row gutter={16} key={'statusBotRow'}>
        <Col span={2}>
          <Form.Item
            name={'botStatus'}
            key={'botStatus'}
            valuePropName="checked"
            label="Ativo"
          >
            <Switch
            />
          </Form.Item>
        </Col>
        {!botStatus &&
          <>
            <Col span={4}>
              <Form.Item
                name={'disableDuration'}
                key={'disableDuration'}
                label="Duração"
              >
                <Select
                  // onChange={handleDisableDurationChange}
                  style={{ width: '100%' }}
                >
                  <Option value="3600">Por 1 hora</Option>
                  <Option value="28800">Por 8 horas</Option>
                  <Option value="86400">Por 24 horas</Option>
                  <Option value="forever">Para sempre</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col>
              {initialValues.botStatus === 'bot_disabled' && (
                <div style={{ marginTop: 35, fontSize: '12px', color: 'red' }}>
                  {initialValues.timeUntilReactivation}
                </div>
              )}
            </Col>
          </>
        }

      </Row>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
        <Button onClick={handleCancel} disabled={isSubmitting} style={{ marginRight: 8, backgroundColor: 'red', color: 'white' }}>
          Cancelar
        </Button>
        <Button type="primary" htmlType="submit" loading={isSubmitting} disabled={isSubmitting}>
          {isEditMode ? "Atualizar" : "Criar"}
        </Button>
      </div>
    </Form>
  );
};

export default CustomerForm;
